import { excludeNullOrUndefined, getBreakdownTitle } from 'checkout/utils'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { isCruiseItem, isTourV1Item } from 'lib/checkout/checkoutUtils'
import createSelector from 'lib/web/createSelector'
import { pluralizeToString } from 'lib/string/pluralize'
import { CABIN_CATEGORIES } from 'constants/cruise'
import moment from 'moment'

import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { getDepositServiceFeeConfig } from 'checkout/selectors/featureConfig/deposit'
import { DepositType } from 'constants/checkout'
import { buildCruiseItemView } from 'checkout/lib/utils/cruises/view'
import { buildCruiseSummaryBreakdown } from 'checkout/lib/utils/cruises/summary'
import { getCheckoutRouteFromURL, getIsCabinRateStep } from 'checkout/lib/utils/cruises/steps'
import { getCruiseCountriesByItinerary, getCruisePortsByItinerary, getCruiseDurationByItinerary } from 'lib/cruises/cruiseUtils'
import { sum, EmptyArray, sortBy, groupBy } from 'lib/array/arrayUtils'

const GUARANTEED_CODES = ['GUAR', 'GTY', 'G00000']

export const getCruiseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.CruiseItem> => items.filter(isCruiseItem),
)

/** SOME RATES HAVE A WAITING LIST CATEGORY AND
 * WE ONLY KNOW WHEN "/CABIN-AVAILABILITY" IS REQUESTED

 * WE NEED TO SAVE THOSE CATEGORIES IN THE STATE AND FILTER
 * THE DATA SO AS TO NOT DISPLAY THE SAME UNAVAILABLE RATE **/
export const getCabinRates = createSelector(
  (state: App.State) => state.cruise.cabinRates,
  (state: App.State) => state.cruise.waitListedCategories,
  (cabinRates, waitListedCategories): Array<App.CruiseCabinRateView> => {
    return cabinRates?.filter((item) =>
      !waitListedCategories.includes(item.componentId)).filter(Boolean) ?? EmptyArray
  },
)

export function getCruiseBreakdownView(
  offerView: App.Checkout.CruiseAccommodationOfferView,
  hidePrice: boolean,
): App.Checkout.PriceBreakdownView {
  const { startDate, endDate, saleUnit, itemViews, mainLabel } = offerView

  const cruiseDuration = getCruiseDurationByItinerary(offerView.offer?.itinerary ?? [])

  const items = itemViews.map<App.Checkout.ItemBreakdownView>(
    (itemView: App.Checkout.CruiseAccommodationItemView, index) => {
      const { additionalElements, additionalInfoText } = buildCruiseSummaryBreakdown(itemView)

      const onboardCreditsV1 = itemView.cabinRateView?.onboardCredits || itemView.cabinHold?.pricing?.onboardCredits
      const onboardCreditsV2 = itemView.cabinSelection?.pricing?.onboardCredit

      const onboardCreditAmount = onboardCreditsV1?.amount || onboardCreditsV2?.amount
      const onboardCreditCurrencyCode = onboardCreditsV1?.currencyCode || onboardCreditsV2?.currency

      const hasOnboardCredits = !!(onboardCreditAmount && onboardCreditCurrencyCode)

      const price = (hidePrice || !window.location.pathname.includes('purchase')) ? undefined : (itemView?.price ?? 0) - itemView.taxesAndFees
      const title = [`${saleUnit} ${index + 1}`, itemView.item?.cabinType].filter(Boolean).join(' - ')

      return {
        title,
        additionalInfoText,
        cancellationPolicy: itemView.cabinHold?.pricing.cancellationPolicy,
        additionalElements,
        price,
        taxesAndFees: itemView.taxesAndFees,
        offerId: itemView.item.offerId,
        itemType: 'cruise',
        ...(hasOnboardCredits && {
          onboardCredits: {
            amount: onboardCreditAmount,
            currencyCode: onboardCreditCurrencyCode,
          },
        }),
      }
    })

  const formattedStartDate = startDate ? `${moment(startDate).format(DMY_CASUAL_FORMAT)}` : 'start'
  const formattedEndDate = endDate ? `${moment(endDate).format(DMY_CASUAL_FORMAT)}` : 'end'
  const price = sum(items, item => (item.price ?? 0))
  const memberPrice = sum(items, item => (item.memberPrice ?? 0))

  return {
    title: getBreakdownTitle(offerView),
    description: mainLabel,
    additionalInfoText: [
      `${offerView.offer?.cruiseLine.name} - ${offerView.offer?.ship.name}`,
      `${formattedStartDate} - ${formattedEndDate} - ${pluralizeToString('night', cruiseDuration - 1)}`,
    ],
    items,
    price,
    memberPrice,
    onboardCredits: items.find(item => item.onboardCredits)?.onboardCredits,
    productType: offerView.offerType,
  }
}

export const getCheckoutCruiseOfferView = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.cruiseOffers,
  (state: App.State) => state.cruise.cabinRates,
  (state: App.State) => state.cruise.cabinRateDetails,
  (state: App.State) => state.cruise.cruiseDeparture,
  (state: App.State) => state.cruise.cabinAvailability,
  (state: App.State) => state.cruise.mapLegend,
  (state: App.State) => state.cruise.deckSelections,
  (state: App.State) => state.cruise.cabinHold,
  getDepositServiceFeeConfig,
  (state: App.State) => state.cruise.cabinPricing,
  (state: App.State) => state.cruise.booking.cabinSelection,
  (state: App.State) => state.cruise.booking.cabinRates,
  (state: App.State) => state.cruise.booking.rateDetailsList,
  (state: App.State) => state.router.location.pathname,
  (
    items,
    offers,
    rates,
    rateDetails,
    departures,
    availableCabinList,
    legendList,
    deckSelections,
    cabinHold,
    serviceFee,
    cabinPricing,
    cabinSelection,
    cabinRates,
    rateDetailsList,
    pathname,
  ): App.WithDataStatus<Array<App.Checkout.CruiseAccommodationOfferView>> => {
    if (items.length === 0) { return { hasRequiredData: true, data: [] } }

    const item = items[0]
    const offer = offers[item.offerId]

    const dayDuration = getCruiseDurationByItinerary(offer?.itinerary)
    const countriesCount = getCruiseCountriesByItinerary(offer?.itinerary).length
    const portsCount = getCruisePortsByItinerary(offer?.itinerary).length

    const cruiseCheckoutStep = getCheckoutRouteFromURL(pathname)
    const isCabinRateStep = getIsCabinRateStep(cruiseCheckoutStep)

    let hasRequiredData = false
    const offerView: App.Checkout.CruiseAccommodationOfferView = {
      offer,
      offerId: item.offerId,
      mainLabel: offer?.name,
      duration: offer?.duration,
      startDate: departures?.[item.departureId]?.departureDate,
      endDate: departures?.[item.departureId]?.returnDate,
      image: offer?.images?.[0],
      location: getLocationString(offer),
      occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
      offerType: offer?.type,
      reservationType: 'instant_booking',
      saleUnit: 'cabin',
      durationLabel: pluralizeToString('night', dayDuration - 1),
      offerLoaded: !!offer,
      itemViews: [],
      propertyTimezone: 'GMT',
      operatorName: offer ? `Operated by: ${offer.cruiseLine.name}` : '',
      shipName: offer?.ship ? `Cruise ship: ${offer.ship.name}` : '',
      countries: countriesCount,
      ports: portsCount,
      serviceFee,
      depositType: DepositType.FLAT,
      designation: 'Cruise',
      ship: offer?.ship,
      availableCabinList: availableCabinList ?? [],
      legendList,
      deckSelections: deckSelections ?? {},
      urgencyLabels: [],
      bedGroups: [],
      confidenceLabels: [],
    }

    if (item.version === 'v2') {
      const depositAmount = cabinSelection?.pricing.paymentSchedule?.firstPayment?.amount ?? null
      const cabinRate = cabinRates ? cabinRates.find(rate => rate.componentId === item.componentId) : undefined

      const details = rateDetails?.[item.componentId || '']
      const itemViewWithStatus = buildCruiseItemView(item, offer, undefined, undefined, details, cabinPricing, cabinSelection, cabinRate)

      // The data required is different according to the checkout stage
      hasRequiredData = isCabinRateStep ? !!offer : [
        itemViewWithStatus.hasRequiredData,
        !!offer,
        !!cabinSelection,
      ].every(Boolean)

      return {
        hasRequiredData,
        data: [{
          ...offerView,
          itemViews: [itemViewWithStatus.data],
          ...(depositAmount && { depositAmount }),
        }],
      }
    }

    const rate = rates ? rates.find(rate => rate.componentId === item.componentId) : undefined
    const depositAmount = cabinPricing?.paymentScheduleDetails?.schedule?.firstPayment?.amount ?? null

    const details = rateDetails?.[item.componentId || '']
    const itemViewWithStatus = buildCruiseItemView(item, offer, rate, cabinHold, details, cabinPricing)

    // The data required is different according to the checkout stage
    hasRequiredData = isCabinRateStep ? !!offer : [
      itemViewWithStatus.hasRequiredData,
      !!availableCabinList && availableCabinList.length > 0,
      !!offer,
      !!rate,
    ].every(Boolean)

    return {
      hasRequiredData,
      data: [{
        ...offerView,
        itemViews: [itemViewWithStatus.data],
        ...(depositAmount && { depositAmount }),
      }],
    }
  },
)

export const getCheckoutCruiseOffer = createSelector(
  getCheckoutCruiseOfferView,
  (offerViewWithStatus): App.CruiseOffer | undefined => offerViewWithStatus.data[0]?.offer,
)

export const getCruisesV1 = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (items, leOffers) => {
    return items.filter(isTourV1Item).filter(item => {
      const offer = leOffers[item.offerId]
      return !!offer && !!offer.holidayTypes?.includes('Cruises')
    })
  },
)

export const isCruiseCheckout = createSelector(
  getCruiseItems,
  getCruisesV1,
  (state: App.State) => state.checkout.cart.existingOrder?.cruiseItems,
  (cruiseItems, cruiseOfTourV1Items, existingCruiseItems) => {
    return !!cruiseItems.length || !!cruiseOfTourV1Items.length || !!existingCruiseItems?.length
  },
)

// BOOKING V2
export const getBookingAllCabinRates = createSelector(
  (state: App.State) => state.cruise.booking.cabinRates,
  (state: App.State) => state.cruise.waitListedCategories,
  (cabinRates, waitListedCategories): Array<Cruises.BookingCabinRate> => {
    return cabinRates?.filter((item) =>
      !waitListedCategories.includes(item.componentId)).filter(Boolean) ?? EmptyArray
  },
)

export const getBookingCabinTypesView = createSelector(
  (state: App.State) => getBookingAllCabinRates(state),
  (state: App.State) => getCheckoutCruiseOffer(state),
  (cabinRates, offer): Array<Cruises.BookingCabinTypeView> => {
    if (!cabinRates || !offer) return []
    const cabinCategories: App.CruiseShipCabinCategory[] = offer.ship.cabinCategories ?? EmptyArray
    const offerEvergreenInclusions = offer.evergreenInclusions
    const ratesByType = groupBy(cabinRates, rate => rate.cabinType)

    return CABIN_CATEGORIES
      .map((cabinType) => {
        const categoryRates = ratesByType.get(cabinType.category) ?? []
        const [lowestRate] = sortBy(categoryRates, rate => rate.priceDetails.price, 'asc')
        const shipCategory = cabinCategories.find(category => category.cabinType === cabinType.category)
        const evergreenInclusions = offerEvergreenInclusions.filter((inclusion) => inclusion.cabinTypes.includes(cabinType.category))

        return {
          name: cabinType.category,
          imageId: shipCategory?.imageId || cabinType.defaultImageId,
          lowestRatePrice: lowestRate?.priceDetails,
          evergreenInclusions,
        }
      })
  },
)

/* Get cabin rates filtered by cabinCode and cabinType from CruiseItem */
export const getBookingCabinRates = createSelector(
  (state: App.State) => state.cruise.booking.cabinRates,
  (state: App.State) => getCruiseItems(state),
  (cabinRates, cruiseItems): Array<Cruises.BookingCabinRate> => {
    const [cruiseItem] = cruiseItems
    if (!cabinRates || !cruiseItem?.cabinCode) return []

    const rates = cabinRates.filter((cabinRate) => (
      cruiseItem.cabinCode === cabinRate.pricedCategoryCode &&
      cabinRate.cabinType === cruiseItem.cabinType
    ))

    return sortBy(rates, (rate) => rate.priceDetails.price, 'asc')
  },
)

export const getBookingCabinCategories = createSelector(
  (state: App.State) => getBookingAllCabinRates(state),
  (state: App.State) => getBookingCabinsByCategory(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => getCheckoutCruiseOffer(state),
  (state: App.State) => state.cruise.ships,
  (cabinRates, cabinsByCategory, cruiseItems, offer, ships): Array<Cruises.BookingCabinCategory> => {
    const [cruiseItem] = cruiseItems
    const categories = offer?.ship.cabinCategories ?? EmptyArray
    const offerEvergreenInclusions = offer?.evergreenInclusions ?? EmptyArray

    if (!cabinRates || !offer || !cruiseItem) return []

    const ship = ships[offer.ship.id]
    const customCabinCategoriesGroups = ship?.cabinCategoriesGroups ?? []

    const categoriesWithPrice = categories.map((category) => {
      const customCategoryGroup = customCabinCategoriesGroups.find(
        (cabinCategoryGroup) => cabinCategoryGroup.cabinCategories?.some(cabinCategory => cabinCategory.code === category.code),
      )
      const categoryImageIds = customCategoryGroup?.images?.map(image => image.imageId) ?? []

      const categoryRatesOfSelectedType = cabinRates.filter((rate) => (
        rate.pricedCategoryCode === category.code &&
        rate.cabinType === cruiseItem?.cabinType
      ))
      const categoryRates = sortBy(
        categoryRatesOfSelectedType,
        (rate) => rate.priceDetails.price,
        'asc',
      )

      const lowestRatePrice = categoryRates[0]?.priceDetails
      const cabins = cabinsByCategory[category.code] ?? EmptyArray
      const cabinsCount = cabins.filter(cabin => !GUARANTEED_CODES.includes(cabin.cabinNumber)).length
      const [cabin] = cabins

      const evergreenInclusions = offerEvergreenInclusions.filter((inclusion) => (
        inclusion.cabinTypes.includes(cruiseItem?.cabinType as string) &&
        inclusion.cabinCategories.includes(category.code)
      ))

      const hasGuaranteedCabin = cabins.some(cabin => GUARANTEED_CODES.includes(cabin.cabinNumber))
      const hasAccessibleCabin = cabins.some(cabin => cabin.isAccessibleCabin)

      const hasConnectingCabin = cabins.some(cabin => (
        cabin.connectingCabinNumber &&
        cabin.connectingCabinNumber !== '*' &&
        cabins.some(c => c.cabinNumber === cabin.connectingCabinNumber)
      ))

      const imageIds = categoryImageIds.length ? categoryImageIds : [category.imageId]
      const images = imageIds.filter(Boolean).map(imageId => ({ id: imageId }))

      const info = category.cabinCategoryInfo?.[0] || {}
      info.description = customCategoryGroup?.description ?? info.description

      const name = info?.name ?? category.cabinType

      return {
        ...category,
        images,
        name,
        info,
        cabin,
        cabinsCount,
        evergreenInclusions,
        lowestRatePrice,
        hasAccessibleCabin,
        hasConnectingCabin,
        hasGuaranteedCabin,
      }
    })
      .filter((category) => category.lowestRatePrice)

    return sortBy(
      categoriesWithPrice,
      (category) => category.lowestRatePrice.price, 'asc',
    )
  },
)

export const getBookingCabinCategoryView = createSelector(
  (state: App.State) => getBookingCabinCategories(state),
  (state: App.State) => state.cruise.ships,
  (state: App.State) => getCheckoutCruiseOffer(state),
  (allCategories, ships, offer): Array<Cruises.BookingCabinCategoryView> => {
    if (!offer) return []

    const ship = ships[offer.ship.id]
    const customCabinCategoriesGroups = ship?.cabinCategoriesGroups ?? []

    const buildCabinCategoriesGroup = (
      groupName: string,
      cabinCategories: Cruises.BookingCabinCategory[],
    ): Cruises.BookingCabinCategoryView => {
      const lowestRatePrice = sortBy(cabinCategories, (category) => category.lowestRatePrice.price, 'asc')[0].lowestRatePrice
      const categories = cabinCategories.map(category => ({
        code: category.code,
        color: category.color ?? '',
      }))

      const { cabin, info, evergreenInclusions, images } = cabinCategories[0]
      const description = info?.description
      const hasAccessibleCabin = cabinCategories.some(category => category.hasAccessibleCabin)
      const hasConnectingCabin = cabinCategories.some(category => category.hasConnectingCabin)
      const hasGuaranteedCabin = cabinCategories.some(category => category.hasGuaranteedCabin)

      /* multiples categories can have guaranteed cabin with the same code,
      * so we need to count them separately to avoid duplicates */
      const normalCabinsCount = sum(cabinCategories, category => category.cabinsCount)
      const cabinsCount = normalCabinsCount + (hasGuaranteedCabin ? 1 : 0)

      const name = groupName

      return {
        name,
        images,
        cabinsCount,
        description,
        categories,
        lowestRatePrice,
        evergreenInclusions,
        hasAccessibleCabin,
        hasConnectingCabin,
        cabin,
      }
    }

    // If we have custom categories groups (comes from Admin Portal), we need to use them
    let customCategoryGroups: Cruises.BookingCabinCategoryView[] = []
    if (customCabinCategoriesGroups.length) {
      customCategoryGroups = customCabinCategoriesGroups.map((group) => {
        const categoryCodes = group.cabinCategories.map(category => category.code)
        const cabinCategories = allCategories.filter(category => categoryCodes.includes(category.code))

        if (!cabinCategories.length) return null

        return buildCabinCategoriesGroup(group.name, cabinCategories)
      }).filter(Boolean) as Cruises.BookingCabinCategoryView[]
    }

    // If we don't have custom categories groups, we use the default ones
    const customCategoryCodes = customCabinCategoriesGroups.map(group => group.cabinCategories.map(category => category.code)).flat()
    const categoriesWithoutCustomGroup = allCategories.filter(category => !customCategoryCodes.includes(category.code))

    const groupedCategories: Map<string, Cruises.BookingCabinCategory[]> = groupBy(
      categoriesWithoutCustomGroup,
      category => category.name.toLowerCase(),
    )
    const cabinCategoryGroups = Array.from(groupedCategories).map(([, cabinCategories]) => {
      const name = cabinCategories[0].name
      return buildCabinCategoriesGroup(name, cabinCategories)
    })

    return sortBy([
      ...customCategoryGroups,
      ...cabinCategoryGroups,
    ], (group) => group.lowestRatePrice.price, 'asc')
  },
)

export const getBookingCabinsByCategory = createSelector(
  (state: App.State) => state.cruise.booking.cabinList,
  (state: App.State) => state.cruise.booking.cabinRates,
  (state: App.State) => state.cruise.booking.cabinDetails,
  (state: App.State) => getCruiseItems(state),
  (cabinList, cabinRates, cabinDetails, cruiseItems): Record<string, Cruises.BookingCabin[]> => {
    const [cruiseItem] = cruiseItems
    if (!cabinRates || !cabinList || !cruiseItem) return {}

    const cabinsWithCategory = Object.values(cabinList)
      .map((cabin) => {
        const rates = cabinRates.filter(rate => cabin.componentIds.includes(rate.componentId))
        const categoryCodes = rates.map(rate => rate.pricedCategoryCode)
        const cabinTypes = rates.map(rate => rate.cabinType)
        const details = cabinDetails?.[cabin.cabinNumber]

        return {
          ...cabin,
          details,
          categoryCodes,
          cabinTypes,
        }
      })

    const cabinsByCategory = cabinsWithCategory.reduce((acc, value) => {
      const { categoryCodes, cabinTypes, ...cabin } = value

      if (!categoryCodes.length || !cabinTypes.includes(cruiseItem.cabinType || '')) return acc

      const values = {}

      for (const categoryCode of categoryCodes) {
        values[categoryCode] = [...(acc[categoryCode] || []), cabin]
      }

      return {
        ...acc,
        ...values,
      }
    }, {}) as Record<string, Cruises.BookingCabin[]>

    return cabinsByCategory
  },
)

export const getBookingCabinsWithPrice = createSelector(
  (state: App.State) => getCheckoutCruiseOffer(state),
  (state: App.State) => state.cruise.booking.cabinList,
  (state: App.State) => state.cruise.booking.cabinRates,
  (state: App.State) => state.cruise.booking.cabinDetails,
  (state: App.State) => getCruiseItems(state),
  (offer, cabinList, cabinRates, cabinDetails, cruiseItems): Cruises.BookingCabinWithPrice[] => {
    const [cruiseItem] = cruiseItems
    if (!offer || !cabinRates || !cabinList || !cruiseItem) return []

    const ratesSelected = cabinRates.filter(rate => (
      cruiseItem.cabinCodes?.includes(rate.pricedCategoryCode) &&
      rate.cabinType === cruiseItem.cabinType
    ))

    return Object.values(cabinList)
      .map((cabin) => {
        const rates = ratesSelected.filter(rate => cabin.componentIds.includes(rate.componentId))
        const sortedRates = sortBy(rates, rate => rate.priceDetails.price, 'asc')
        const lowestPrice = sortedRates[0]?.priceDetails
        const categoryCode = sortedRates[0]?.pricedCategoryCode
        const isGuaranteed = GUARANTEED_CODES.includes(cabin.cabinNumber) || !cabin.deckId
        const details = cabinDetails?.[cabin.cabinNumber]

        const isByronBeachClub = offer.evergreenInclusions.some((inclusion) => (
          inclusion.cabinTypes.includes(cruiseItem.cabinType as string) &&
          inclusion.cabinCategories.includes(categoryCode) &&
          inclusion.inclusion === 'Access to Byron Beach Club'
        ))

        const connectingCabinAvailable = !!cabin.connectingCabinNumber && !!cabinList[cabin.connectingCabinNumber]

        return {
          ...cabin,
          details,
          categoryCode,
          isGuaranteed,
          isByronBeachClub,
          connectingCabinAvailable,
          lowestPrice,
        }
      })
      .filter(cabin => cabin.lowestPrice)
  },
)

export const getDecksAvailable = createSelector(
  (state: App.State) => getCheckoutCruiseOffer(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => getBookingCabinsWithPrice(state),
  (offer: App.CruiseOffer | undefined, cruiseItems, cabinsWithPrice): Array<App.CruiseDeck> => {
    const [cruiseItem] = cruiseItems
    if (!offer || !cabinsWithPrice || !cruiseItem) return []

    // We want to display the decks even if there are no cabins
    // this reduce also removes duplicate decks that have the same name
    const decksByName = offer.ship.decks?.reduce((acc, deck) => {
      const cabins = cabinsWithPrice.filter(cabin => (
        cabin.deckId === String(deck.externalId) &&
        cruiseItem.cabinCodes?.includes(cabin.categoryCode) &&
        !cabin.isGuaranteed
      ))

      const groupedDeck: App.CruiseDeck | null = acc[deck.name]
      if (groupedDeck && !!groupedDeck.cabinAmount) {
        return acc
      }

      return {
        ...acc,
        [deck.name]: {
          deckId: String(deck.externalId),
          deckName: deck.name || 'Deck',
          deckNumber: deck.deckNumber,
          image: deck.imageId ?? '',
          shipCabinPositionList: deck.shipCabinPosition ?? [],
          cabinAmount: cabins.length,
        },
      }
    }, {}) ?? {}

    return sortBy(Object.values(decksByName), deck => deck.deckNumber, 'desc')
  },
)

export const getBookingCabinSelectionView = createSelector(
  (state: App.State) => getBookingCabinsWithPrice(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => getCheckoutCruiseOffer(state),
  (state: App.State) => getBookingCabinCategories(state),
  (state: App.State) => state.cruise.booking.cabinListLoading,
  (state: App.State) => state.cruise.booking.rateListLoading,
  (cabinsWithPrice, cruiseItems, offer, categories, cabinListLoading, rateListLoading): Cruises.BookingCabinSelectionView | null => {
    const cruiseItem = cruiseItems[0]
    const cabinCategoryCodes = cruiseItem?.cabinCodes ?? EmptyArray

    if (offer && cabinCategoryCodes.length && cruiseItem?.cabinType) {
      const allCabins = cabinsWithPrice
        .filter(cabin => !cabin.isGuaranteed)

      const cabinsByCategory = Array.from(groupBy(allCabins, cabin => cabin.categoryCode))
        .map(([code, cabins]) => {
          const category = categories.find(category => category.code === code)

          if (category) {
            const lowestRatePrice = sortBy(cabins, cabin => cabin.lowestPrice?.price, 'asc')[0]?.lowestPrice

            const name = category.name
            const color = category.color ?? ''
            const images = category.images
            const description = category.info?.description
            const evergreenInclusions = category.evergreenInclusions

            const cabinsCount = cabins.length

            return {
              name,
              code,
              color,
              cabins,
              description,
              images,
              cabinsCount,
              evergreenInclusions,
              lowestRatePrice,
            }
          }

          return null
        }).filter(Boolean) as Cruises.BookingCabinsByCategoryView[]

      const sortedCabinsByCategory = sortBy(cabinsByCategory, category => category.lowestRatePrice?.price, 'asc')
      const lowestCabinPrice = sortBy(allCabins, cabin => cabin.lowestPrice?.price, 'asc')[0]?.lowestPrice

      const guaranteedCabin = cabinsWithPrice.find(cabin => cabin.isGuaranteed)
      let guaranteedCabinImageId: string = ''

      if (guaranteedCabin) {
        const category = categories.find(category => category.code === guaranteedCabin.categoryCode)
        guaranteedCabinImageId = category?.images[0]?.id ?? ''
      }

      return {
        isLoading: cabinListLoading || rateListLoading,
        shipId: offer.ship.id,
        cabins: allCabins,
        cabinsByCategory: sortedCabinsByCategory,
        guaranteedCabin,
        guaranteedCabinImageId,
        lowestCabinPrice,
      }
    }

    return null
  },
)

export const getCruiseDepositDetailsFromCabinHold = createSelector(
  (state: App.State) => state.cruise.cabinHold,
  (state: App.State) => state.cruise.booking.cabinSelection,
  (cabinHold, cabinSelection): Cruises.CruiseDepositDetails | null => {
    if (cabinHold) {
      const deposit = cabinHold.pricing
        .paymentSchedule.find((item) => item.typeId === 1)

      if (!deposit) return null

      return {
        dueDate: deposit.date,
        amount: deposit.amount,
        currencyCode: cabinHold.pricing.currency,
      }
    }

    if (cabinSelection) {
      const deposit = cabinSelection.pricing.paymentSchedule?.firstPayment

      if (!deposit) return null

      return {
        amount: deposit.amount,
        dueDate: deposit.dueDate,
        currencyCode: deposit.currencyCode,
      }
    }

    return null
  },
)

export const getCruiseOnboardCreditDetails = createSelector(
  (state: App.State) => state.cruise.cabinHold,
  (state: App.State) => state.cruise.booking.cabinSelection,
  (state: App.State) => state.cruise.cabinPricing,
  (cabinHold, cabinSelection, cabinPricing): Cruises.CruiseOnboardCreditDetails | null => {
    if (cabinPricing) {
      const onboardCredits = cabinPricing.pricing.onboardCredits

      if (!onboardCredits) return null

      return {
        amount: onboardCredits.amount,
        currencyCode: onboardCredits.currencyCode,
      }
    }

    if (cabinHold) {
      const onboardCredits = cabinHold.pricing.onboardCredits

      if (!onboardCredits) return null

      return {
        amount: onboardCredits.amount,
        currencyCode: onboardCredits.currencyCode,
      }
    }

    if (cabinSelection) {
      const onboardCredits = cabinSelection.pricing.onboardCredit

      if (!onboardCredits?.amount || !onboardCredits?.currency) return null

      return {
        amount: onboardCredits.amount,
        currencyCode: onboardCredits.currency,
      }
    }

    return null
  },
)

export const getBookingCabinPositions = createSelector(
  (state: App.State) => state.cruise.deckSelections,
  (state: App.State) => getCheckoutCruiseOffer(state),
  (state: App.State) => getCruiseItems(state),
  (deckSelections, offer, cruiseItems): Array<App.ShipCabinPosition> => {
    const [cruiseItem] = cruiseItems
    if (!offer || !deckSelections || !cruiseItem?.deckId) return []

    const deckDetails = deckSelections[cruiseItem.deckId]
    return deckDetails?.shipCabinPosition ?? EmptyArray
  },
)
